import { shouldUserRequestOrganizationMe, useAuth0 } from 'utils/auth'
import { useEffect, useMemo, useRef, useState } from 'react'

import constate from 'constate'
import i18n from 'translations/i18n'
import moment from 'moment-timezone'
import { isEmbedPurchaseFlowPath, isEmbedRoute } from 'constants/router'
import { trackUser } from 'utils/analytics'
import { useLocation } from 'react-router-dom'
import { useUserData } from 'components/contexts/UserDataContext'
import { useUserMe } from 'dataQueries'
import { applyLanguageFromUserMe } from 'utils/localization/applyLanguageChange'
import { logger } from 'utils/logging'

/** /user/me statuses that mean "no platform user yet" (signup, unverified e-mail), not a failure */
const USER_ME_SIGNUP_STATUSES = [401, 403, 404]
/** Statuses that put the session in signup. Not 403: that refuses an existing user until the e-mail is verified */
const USER_ME_NEW_USER_STATUSES = [401, 404]

const useAppMetaSetup = () => {
  const { loading, isAuthenticated, user } = useAuth0()
  const { organizationMe } = useUserData()

  // Prefix-matched, not an exact route match: PF v2 gave the embed sub-routes, so an exact match on
  // the entry path went false the moment the client left the first screen — and this effect then
  // applied the broker's PROFILE language over the ?lang Propstack forced. Trailing slash tolerated.
  const { pathname } = useLocation()
  const isEmbedPurchaseFlow = isEmbedPurchaseFlowPath(pathname)

  const userMeQuery = useUserMe(isAuthenticated)
  const userData = useMemo(() => userMeQuery.data ?? null, [userMeQuery.data])

  // The last error /user/me settled with. A refetch of an errored query without data resets it to
  // pending with error=null, so this keeps the outcome across that window instead of the screen
  // falling through to SignupRegistration until the refetch settles.
  const [settledUserMeError, setSettledUserMeError] = useState<unknown>(null)
  // Forgotten once it no longer describes this load: signed out, answered, or reset (errorUpdatedAt back to 0)
  const isSettledUserMeErrorStale = !isAuthenticated || userMeQuery.isSuccess || !userMeQuery.errorUpdatedAt
  if (!isSettledUserMeErrorStale && userMeQuery.isError && userMeQuery.error !== settledUserMeError) setSettledUserMeError(userMeQuery.error)
  if (isSettledUserMeErrorStale && settledUserMeError) setSettledUserMeError(null)
  // Once /user/me answered 401/404 or an id-less 2xx the user is in signup, and a later failure keeps them there (the form's own poll recovers it)
  const [hasSeenSignupStatus, setHasSeenSignupStatus] = useState(false)
  const isSettledSignupStatus = isAuthenticated && (
    (userMeQuery.isError && USER_ME_NEW_USER_STATUSES.includes((userMeQuery.error as any)?.response?.status))
    || (userMeQuery.isSuccess && !userMeQuery.data?.id)
  )
  if (isSettledSignupStatus && !hasSeenSignupStatus) setHasSeenSignupStatus(true)
  if (!isAuthenticated && hasSeenSignupStatus) setHasSeenSignupStatus(false)

  // Side effects from userMe data
  useEffect(() => {
    if (!userData) return
    trackUser(userData)
    if (userData.defaultTimezone) moment.tz.setDefault(userData.defaultTimezone)
    // In embed mode the forced ?lang locale (applied by the i18n detector at boot) must stay in force,
    // so the profile language never overrides it — for both the i18n strings and moment's date locale.
    if (isEmbedPurchaseFlow) {
      moment.locale(i18n.language)
    } else {
      if (userData.language) moment.locale(userData.language)
      applyLanguageFromUserMe(userData.language)
    }
  }, [userData, isEmbedPurchaseFlow])

  const isUserLoaded = useMemo(() => !loading && isAuthenticated && !!user && userMeQuery.isSuccess && !!userData, [isAuthenticated, loading, user, userData, userMeQuery.isSuccess])
  const isOrganizationMeSupposedToLoad = useMemo(() => shouldUserRequestOrganizationMe(userData), [userData])
  // Only the FIRST /user/me load may hold the app on LoadingPage. Once the query has settled
  // in error at least once (errorUpdatedAt > 0), later isPending flips must not re-enter the
  // loading gate: mounting a new observer on an errored query (e.g. SignupRegistration's own
  // useUserMe) refetches and reports pending again, which unmounted the signup form back into
  // LoadingPage in an infinite oscillation (ISSUE-2266).
  const isUserMeLoading = useMemo(() => isAuthenticated && userMeQuery.isPending && !userMeQuery.errorUpdatedAt, [isAuthenticated, userMeQuery.isPending, userMeQuery.errorUpdatedAt])
  // "set" iff we actually resolved a platform user. A new/unprovisioned user (404/401/403, or a 2xx
  // with a null body) has no userData, so isUserSet is false and RouteSwitch renders SignupRegistration
  // — the only place POST /user/ is fired (ISSUE-2266). Retained data across background-refetch errors
  // keeps an established user out of the signup flow on a transient /user/me blip.
  // Gate on the id, not object truthiness: a 2xx with an empty body (e.g. content negotiation for a
  // role-less user resolving to a non-client handler) must not masquerade as a provisioned user.
  const isUserSet = useMemo(() => !isAuthenticated || !!userData?.id, [isAuthenticated, userData])
  // /user/me could not be answered (5xx, network, any status other than the signup statuses
  // 401/403/404), there is no platform user to fall back on and the session never reached signup.
  // Not a new user, so RouteSwitch shows an error page rather than SignupRegistration.
  const isUserMeFailed = useMemo(() => {
    if (!isAuthenticated || hasSeenSignupStatus || userMeQuery.isSuccess || userData?.id || !settledUserMeError) return false
    const status = (settledUserMeError as any)?.response?.status
    return !USER_ME_SIGNUP_STATUSES.includes(status)
  }, [isAuthenticated, hasSeenSignupStatus, userMeQuery.isSuccess, userData, settledUserMeError])
  const hasLoggedUserMeFailure = useRef(false)
  const { failureCount: userMeFailureCount } = userMeQuery
  useEffect(() => {
    if (!isUserMeFailed || hasLoggedUserMeFailure.current) return
    hasLoggedUserMeFailure.current = true
    logger.error('Startup /user/me failed; showing the error page', undefined, {
      context: {
        component: 'AppMeta',
        action: 'userMeFailed',
        metadata: {
          status: (settledUserMeError as any)?.response?.status ?? 'network',
          isEmbed: isEmbedRoute(pathname),
          failureCount: userMeFailureCount,
        },
      },
    })
  }, [isUserMeFailed, settledUserMeError, pathname, userMeFailureCount])
  const isOrganizationMeLoading = useMemo(() => isAuthenticated && isUserSet && organizationMe.isPending, [isAuthenticated, isUserSet, organizationMe.isPending])
  const isLoading = useMemo(() => loading || isUserMeLoading || (isUserLoaded && isOrganizationMeSupposedToLoad && isOrganizationMeLoading), [loading, isUserMeLoading, isUserLoaded, isOrganizationMeSupposedToLoad, isOrganizationMeLoading])
  const isUserVerified = useMemo(() => !user || !!(user && user.email_verified), [user])

  // /user/me refuses (403) to link an existing platform user until the e-mail is verified, and nothing else re-asks it once it is
  const { refetch: refetchUserMe } = userMeQuery
  const wasUserUnverified = useRef(!isUserVerified)
  useEffect(() => {
    if (wasUserUnverified.current && user?.email_verified) refetchUserMe()
    wasUserUnverified.current = !isUserVerified
  }, [user, isUserVerified, refetchUserMe])

  return {
    isLoading,
    isUserVerified,
    isUserSet,
    isUserMeFailed,
  }
}

export const [AppMetaProvider, useAppMeta] = constate(useAppMetaSetup)
